import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { ArrowLeft, Wrench } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MAINTENANCE_MESSAGE } from '../../components/UI/MaintenanceAlert';
import FeatureFlagsService, {
  MAINTENANCE_BANNER_KEY,
} from '../../service/FeatureFlagsService';
import { useFeatureFlagsStore } from '../../store/storeFeatureFlags';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';

const MaintenanceNoticePage = () => {
  const isEnabled = useFeatureFlagsStore(
    state => state.isMaintenanceBannerEnabled,
  );
  const fetchFlags = useFeatureFlagsStore(state => state.fetchFlags);
  const setNotification = useNotificationStore(state => state.setNotification);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchFlags().finally(() => setIsLoading(false));
  }, [fetchFlags]);

  const handleToggle = async (enabled: boolean) => {
    setIsSaving(true);
    try {
      await FeatureFlagsService.setEnabled(MAINTENANCE_BANNER_KEY, enabled);
      await fetchFlags();
      setNotification({
        type: NotificationType.Success,
        message: `Maintenance notice ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      setNotification({
        type: NotificationType.Error,
        message:
          error instanceof Error
            ? error.message
            : 'Failed to update maintenance notice',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#3650DE',
        py: { xs: 4, md: 8 },
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 680, mx: 'auto' }}>
        <Button
          component={Link}
          to='/admin'
          startIcon={<ArrowLeft size={18} />}
          sx={{ mb: 3, color: '#fff', textTransform: 'none' }}
        >
          Back to Admin Dashboard
        </Button>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Stack direction='row' spacing={2} alignItems='center' sx={{ p: 3 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isEnabled ? 'warning.light' : 'action.hover',
                color: isEnabled ? 'warning.dark' : 'text.secondary',
                flexShrink: 0,
              }}
            >
              <Wrench size={26} weight='fill' />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>
                  Maintenance Notice
                </Typography>
                <Chip
                  size='small'
                  label={isEnabled ? 'Active' : 'Inactive'}
                  color={isEnabled ? 'warning' : 'default'}
                  variant={isEnabled ? 'filled' : 'outlined'}
                />
              </Stack>
              <Typography variant='body2' color='text.secondary'>
                Show a maintenance banner to every logged-in user.
              </Typography>
            </Box>
          </Stack>
          <Divider />
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {isEnabled ? 'Banner is visible' : 'Banner is hidden'}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {isEnabled
                      ? 'Users currently see the maintenance message.'
                      : 'Turn on to display the maintenance message.'}
                  </Typography>
                </Box>
                <Switch
                  checked={isEnabled}
                  disabled={isSaving}
                  onChange={event => handleToggle(event.target.checked)}
                />
              </Stack>
              <Typography
                variant='overline'
                color='text.secondary'
                sx={{ display: 'block', mt: 3, mb: 1 }}
              >
                Preview
              </Typography>
              <Alert severity='warning' sx={{ borderRadius: 2 }}>
                {MAINTENANCE_MESSAGE}
              </Alert>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default MaintenanceNoticePage;
